import { useRef, useCallback } from 'react';

const displayI18n = (v, lang = 'en') => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  return v[lang] || v.en || Object.values(v).find(x => x) || '';
};

const CardPositioner = ({ bgUrl, cards, activeCardIndex, onUpdatePosition, onSelectCard, onUpdateTapArea, placeholder }) => {
  const containerRef = useRef(null);
  const dragRef = useRef(null);

  const handleClick = useCallback((e) => {
    if (activeCardIndex < 0 || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    onUpdatePosition(activeCardIndex, Math.round(x * 10) / 10, Math.round(y * 10) / 10);
  }, [activeCardIndex, onUpdatePosition]);

  const handleDotClick = useCallback((e, i) => {
    e.stopPropagation();
    if (onSelectCard) onSelectCard(i);
  }, [onSelectCard]);

  const round1 = (v) => Math.round(v * 10) / 10;

  const handleDragStart = useCallback((e, cardIndex, type) => {
    e.stopPropagation();
    e.preventDefault();
    if (!containerRef.current) return;
    const cRect = containerRef.current.getBoundingClientRect();
    const card = cards[cardIndex];
    dragRef.current = {
      type, cardIndex,
      startClientX: e.clientX, startClientY: e.clientY,
      startPosX: card.position_x, startPosY: card.position_y,
      startW: card.tap_area_width || 15, startH: card.tap_area_height || 15,
      cRect,
    };

    const handleMove = (me) => {
      me.preventDefault();
      if (!dragRef.current) return;
      const d = dragRef.current;
      const dx = ((me.clientX - d.startClientX) / d.cRect.width) * 100;
      const dy = ((me.clientY - d.startClientY) / d.cRect.height) * 100;

      if (d.type === 'move') {
        onUpdatePosition(d.cardIndex,
          Math.max(0, Math.min(100, round1(d.startPosX + dx))),
          Math.max(0, Math.min(100, round1(d.startPosY + dy))));
        return;
      }

      // Corner resize — opposite corner stays fixed
      const nwX = d.startPosX - d.startW / 2;
      const nwY = d.startPosY - d.startH / 2;
      const seX = d.startPosX + d.startW / 2;
      const seY = d.startPosY + d.startH / 2;
      let newW, newH, newCX, newCY;

      if (d.type === 'se') {
        newW = Math.max(5, seX + dx - nwX); newH = Math.max(5, seY + dy - nwY);
        newCX = nwX + newW / 2; newCY = nwY + newH / 2;
      } else if (d.type === 'nw') {
        newW = Math.max(5, seX - (nwX + dx)); newH = Math.max(5, seY - (nwY + dy));
        newCX = seX - newW / 2; newCY = seY - newH / 2;
      } else if (d.type === 'ne') {
        newW = Math.max(5, seX + dx - nwX); newH = Math.max(5, seY - (nwY + dy));
        newCX = nwX + newW / 2; newCY = seY - newH / 2;
      } else { // sw
        newW = Math.max(5, seX - (nwX + dx)); newH = Math.max(5, seY + dy - nwY);
        newCX = seX - newW / 2; newCY = nwY + newH / 2;
      }

      onUpdatePosition(d.cardIndex, round1(newCX), round1(newCY));
      if (onUpdateTapArea) onUpdateTapArea(d.cardIndex, round1(newW), round1(newH));
    };

    const handleUp = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };

    window.addEventListener('pointermove', handleMove, { passive: false });
    window.addEventListener('pointerup', handleUp);
  }, [cards, onUpdatePosition, onUpdateTapArea]);

  const corners = ['nw', 'ne', 'sw', 'se'];
  const cornerStyles = {
    nw: { top: -16, left: -16, cursor: 'nwse-resize' },
    ne: { top: -16, right: -16, cursor: 'nesw-resize' },
    sw: { bottom: -16, left: -16, cursor: 'nesw-resize' },
    se: { bottom: -16, right: -16, cursor: 'nwse-resize' },
  };

  return (
    <div ref={containerRef} onClick={handleClick} data-testid="card-positioner"
      className="relative w-full aspect-video rounded-xl overflow-hidden border-2 border-dashed border-slate-300 bg-slate-100 cursor-crosshair select-none"
      style={{ touchAction: 'none' }}>
      {bgUrl ? (
        <img src={bgUrl} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
      ) : (
        <div className="flex items-center justify-center h-full text-slate-400 text-sm">
          {placeholder || 'Click to position the selected card'}
        </div>
      )}

      {/* Tap-area overlays for hotspot/invisible cards */}
      {cards.map((card, i) => {
        const isSpot = card.card_appearance === 'hotspot' || card.card_appearance === 'invisible';
        if (!isSpot) return null;
        const tw = card.tap_area_width || 0;
        const th = card.tap_area_height || 0;
        if (!tw && !th) return null;
        const isActive = i === activeCardIndex;
        return (
          <div key={`area-${i}`}
            className={`absolute rounded-lg ${isActive ? 'border-2 border-[#0077FF] bg-[#0077FF]/10' : 'border border-[#0077FF]/30 bg-[#0077FF]/5'}`}
            style={{
              left: `${card.position_x - tw / 2}%`, top: `${card.position_y - th / 2}%`,
              width: `${tw}%`, height: `${th}%`,
              cursor: isActive ? 'move' : 'pointer',
              zIndex: isActive ? 20 : 5,
              touchAction: 'none',
            }}
            onClick={(e) => { e.stopPropagation(); onSelectCard(i); }}
            onPointerDown={isActive ? (e) => handleDragStart(e, i, 'move') : undefined}
            data-testid={`tap-area-overlay-${i}`}>
            {isActive && (
              <span className="absolute top-1 left-1 text-[8px] font-bold text-[#0077FF]/60 uppercase pointer-events-none select-none">Tap area</span>
            )}
            {isActive && corners.map(c => (
              <div key={c}
                className="absolute w-8 h-8 rounded-full bg-[#0077FF] border-2 border-white shadow-lg"
                style={{ ...cornerStyles[c], touchAction: 'none', cursor: cornerStyles[c].cursor, zIndex: 25 }}
                onPointerDown={(e) => handleDragStart(e, i, c)}
                data-testid={`tap-area-handle-${c}-${i}`} />
            ))}
          </div>
        );
      })}

      {/* Card position dots */}
      {cards.map((card, i) => (
        <div key={i}
          onClick={(e) => handleDotClick(e, i)}
          className={`absolute w-4 h-4 rounded-full -translate-x-1/2 -translate-y-1/2 border-2 border-white shadow-lg transition-all cursor-pointer ${
            i === activeCardIndex ? 'bg-[#0077FF] scale-125 ring-4 ring-[#0077FF]/20 z-10' : 'bg-slate-600 scale-100 hover:scale-110'
          }`}
          style={{ left: `${card.position_x}%`, top: `${card.position_y}%` }}
          data-testid={`pos-dot-${i}`}>
          <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-white bg-black/60 px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none">
            {displayI18n(card.label, 'en') || `Card ${i + 1}`}
          </span>
        </div>
      ))}
    </div>
  );
};

export default CardPositioner;
